import { VesselPlansService } from "../services/vessel-plans-service";
import { VesselNotificationsService } from "../services/vessel-notifications-service";
import { BackGroundOptimizationModelBase } from "./vessel-background-optimization-model";
import { VesselPlanningModel, VoyagePlan } from "./vessel-planning-model";
import { VoyageImoModel } from "./voyage-imo-model";
import { RoutingJobOptions, VoyageRoute, VoyageRoutingModel } from "./voyage-routing-model";
import { VoyageSchedulesModel } from "./voyage-schedules-model";
import { _checkRouteAlignmentToStarRoutes } from "../utils/route-utils";
import { isWithinBprReasonWindow } from "../utils/helpers";
import { IndexedDBSyncService } from "../../indexed-db/services/sync_db_service";

export const ALL_BACKGROUND_SYNC_COMPLETED = 'all-bg-sync-data-completed'


export class BackGroundOptimizationModelImpl extends BackGroundOptimizationModelBase {
   
    constructor() {
        super();

        // Listening for background sync completion
        window.addEventListener(ALL_BACKGROUND_SYNC_COMPLETED, async (event) => {
            const evt = event as CustomEvent;
            console.info("*** ALL BACKGROUND SYNC COMPLETED : MANUAL ***", evt.detail);
            // Fetch the latest plans for the schedule IDs that were synced, if provided in the event detail
            if (evt.detail && evt.detail.scheduleIds) {
                await this.getLatestPlansFromServer(evt.detail.scheduleIds);
            }
            // Process notifications after sync: check alignment/fuel efficiency and create or close notifications accordingly
            if (evt.detail && evt.detail.notificationsInfo) {
                await this.processNotificationsAfterSync(evt.detail.notificationsInfo);
            }
        });

    }

    private async getLatestPlansFromServer(scheduleIds: string[]) {
            
        try{
            const plans = await VesselPlansService.getPlansByScheduleId(scheduleIds) || [];  
            if(!plans) {
                console.info("No plans found for schedule IDs in Background RouteOptimizations:", scheduleIds);
                return;
            }
            await this.checkForRecalculations(plans);
        } catch (error) {
            console.error("Error fetching plans from server:", error);
        }
    }

    /**
     * Process notifications after background sync completes.
     * 
     * For 'route' type notifications:
     *   - Always compute isAligned and isFuelEfficient, then call createAndSubmitRouteNotification.
     * 
     * For 'BprReject' type notifications:
     *   - Check if the selected route is aligned to a star route OR more fuel efficient.
     *   - If either check passes → drop the BPR reject notification (no action needed).
     *   - If both checks fail → close the BPR reject notification (state: 'Closed' with reason/comments).
     */
    private async processNotificationsAfterSync(notificationsInfo: Record<string, { data: any; type: string; id?: string; imoNo?: string; storeId?: string }[]>) {
        for (const scheduleId of Object.keys(notificationsInfo)) {
            try {
                // Fetch the latest plan for this schedule to get selected route and terminal codes
                const plan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
                if (!plan) {
                    console.warn("No plan found for scheduleId, skipping notifications:", scheduleId);
                    continue;
                }

                const selectedRoute = this.getSelectedRoute(plan);
                if (!selectedRoute) continue;

                

                // Compute alignment and fuel efficiency once per schedule
                const { isAligned, isFuelEfficient, isWithinReasonWindow, routeDistance, routeFuelConsumption } = await this.computeRouteMetrics(plan, selectedRoute, scheduleId);

                console.info(`[BG Sync] Schedule ${scheduleId}: isAligned=${isAligned}, isFuelEfficient=${isFuelEfficient}, routeDistance=${routeDistance}, routeFuel=${routeFuelConsumption}`);

                for (const notification of notificationsInfo[scheduleId]) {
                    await this.processNotification(notification, scheduleId, plan, selectedRoute, isAligned, isFuelEfficient, isWithinReasonWindow, routeDistance, routeFuelConsumption);
                }
            } catch (error) {
                console.error("Error processing notifications for scheduleId:", scheduleId, error);
            }
        }
    }

    private getSelectedRoute(plan: VoyagePlan): VoyageRoute | null {
        const selectedRoute = plan.selectedRouteIndex >= 0 ? plan.routes[plan.selectedRouteIndex] : null;
        if (!selectedRoute) {
            console.warn("No selected route found for scheduleId, skipping notifications:", plan.scheduleId);
            return null;
        }
        return selectedRoute;
    }

    private async computeRouteMetrics(plan: VoyagePlan, selectedRoute: VoyageRoute, scheduleId: string) {
        const isAligned = await _checkRouteAlignmentToStarRoutes(
            selectedRoute,
            plan.departureTerminalCode,
            plan.arrivalTerminalCode,
            scheduleId
        );
        const isFuelEfficient = this._isMoreFuelEfficientThanStarRoute(plan) || false;
        const isWithinReasonWindow = this._isWithinReasonWindow(plan);
        const KmToNM = 1 / 1.852;
        const routeDistance = selectedRoute?.route_info?.totals?.non_eca?.distance
            ? selectedRoute.route_info.totals.non_eca.distance * KmToNM
            : -1;
        const routeFuelConsumption = selectedRoute?.route_info?.totals?.non_eca?.fuel ?? -1;
        return { isAligned, isFuelEfficient, isWithinReasonWindow, routeDistance, routeFuelConsumption };
    }

    private async processNotification(notification: { data: any; type: string; id?: string; imoNo?: string; storeId?: string }, scheduleId: string, plan: VoyagePlan, selectedRoute: VoyageRoute, isAligned: boolean, isFuelEfficient: boolean, isWithinReasonWindow: boolean, routeDistance: number, routeFuelConsumption: number) {
        try {
            if (notification.type === 'Route') {
                if (isWithinReasonWindow && this._isWithinReasonWindow(plan)) {
                    await VesselNotificationsService.createAndSubmitRouteNotification(
                        scheduleId,
                        plan.id ?? 0,
                        plan.eta,
                        plan.etd,
                        routeDistance,
                        routeFuelConsumption,
                        isAligned,
                        isFuelEfficient,
                        plan.departureTerminalCode,
                        plan.arrivalTerminalCode,
                        selectedRoute?.weatherRoutingInfo
                    );
                    console.info(`Route notification created successfully for scheduleId:`, scheduleId);
                }
                if (notification.storeId) {
                    await IndexedDBSyncService.request.deleteRequest(notification.storeId);
                }
            } else if (notification.type === 'BprReject') {
                if (isWithinReasonWindow && !isAligned && !isFuelEfficient) {
                    const parsedData = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
                    const optimizedBPRs = await VesselPlansService.getOptimizedBPRsByPortPair(plan.departureTerminalCode, plan.arrivalTerminalCode, scheduleId);
                    const bprRankIDs = (optimizedBPRs ?? []).map(bpr => bpr.best_practice_route_id);
                    await VesselNotificationsService.createRouteSelectNotification(
                        scheduleId ?? '',
                        plan.departureTerminalCode ?? '', 
                        plan.arrivalTerminalCode ?? '', 
                        plan.id ?? 0,
                        {reason: parsedData?.reason, comment: parsedData?.comments,navWarningNo: parsedData?.navWarningNo},
                        plan.eta ?? "", 
                        plan.etd ?? "", 
                        bprRankIDs
                    );
                }
                if (notification.storeId) {
                    await IndexedDBSyncService.request.deleteRequest(notification.storeId);
                    console.info(`Removed BprReject notification entry from bg-sync-api-store:`, notification.storeId);
                }
            }
        } catch (error) {
            console.error(`Error processing ${notification.type} notification after sync for scheduleId:`, scheduleId, error);
        }
    }


    /**
     * Check if the selected route is more fuel efficient than ALL star (system) routes in the plan.
     * Replicates the logic from voyage-passage-plan-overview.ts.
     * Returns true only if own route fuel < every star route's fuel.
     */
    private _isMoreFuelEfficientThanStarRoute(plan: VoyagePlan): boolean {
        const selectedRoute = plan.selectedRouteIndex >= 0 ? plan.routes[plan.selectedRouteIndex] : null;
        if (!selectedRoute) return false;

        // If the selected route is itself a system (star) route, no comparison needed
        if (selectedRoute.routeCreatedBy === 'system') return false;

        const bestPracticeRoutes = plan.routes.filter(route => route.routeCreatedBy === 'system');
        if (!bestPracticeRoutes.length) return false;

        const ownRouteFuel = selectedRoute?.route_info?.totals?.non_eca?.fuel;
        if (!ownRouteFuel) return false;

        for (const bpr of bestPracticeRoutes) {
            const bprFuel = bpr?.route_info?.totals?.non_eca?.fuel;
            if (bprFuel !== undefined && ownRouteFuel >= bprFuel) {
                return false; // Star route is more or equally fuel-efficient
            }
        }
        return true;
    }

    // The reason for not choosing a Star Route is collected until one day after ETD (never after ETA).
    private _isWithinReasonWindow(plan: VoyagePlan): boolean {
        // log warning if etd/eta is string so it can be visible in grafana, as they are expected to be numbers
        if (typeof plan?.etd === 'string' || typeof plan?.eta === 'string') {
            console.warn('ETD or ETA is a string, expected to be a number. Attempting to parse it to a timestamp, for schedule_id and imo.', {
                etd: plan?.etd, eta: plan?.eta, scheduleId: plan?.scheduleId, imo: plan?.imoNo
            });
        }
        return isWithinBprReasonWindow(plan?.etd, plan?.eta);
    }

    private async checkForRecalculations(plans: VoyagePlan[]) {
        for (const plan of plans) {
            if(!plan.routes.length) {
                console.info("No routes found for plan in Background RouteOptimizations, schedulId:", plan.scheduleId, plan.id);
                continue;
            }

            plan.routes.forEach((route) => {
                // Check if the route needs recalculation
                if (route.recalcNeeded || (plan.currentSelectedRouteId === route.routeId)) {
                    const routeIndex = plan.routes.findIndex(r => r.routeId === route.routeId);
                    const wasPlanApproved = plan.status === 'approved';
                    const shouldRecalculate = this._checkIfweNeedToCalclateTwice(route, plan);
                    const options: RoutingJobOptions = {
                        routeId: route!.routeId,
                        planScheduleId: plan.scheduleId,
                        updatePlan: {
                            routeIndex: routeIndex,
                            autoApprove: wasPlanApproved,
                        },
                        draftFore: plan!.foreDraft,
                        draftAft: plan!.aftDraft,
                        gmt: plan!.gmt !== null ? plan!.gmt : undefined,
                        etd: plan!.etd,
                        eta: plan!.eta,
                        numberOfReefers: plan!.numberOfReefers,
                        routeType: "WeatherOptimized",
                        whaleZonesApplied: route!.route_info?.whaleZonesApplied,
                        disableWhaleZoneSpeedRestrictions: route!.route_info?.disableWhaleZoneSpeedRestrictions,
                        passagePlan: route!.passagePlan,
                        shouldRecalculate: shouldRecalculate,
                    }

                    VoyageRoutingModel?.postRoutingJobUsingRoute(route, options)
                }
            })
        }

    }

    private _checkIfweNeedToCalclateTwice(route: VoyageRoute, plan: VoyagePlan): boolean {
        //check if first waypoint is_passed is false or null then do it twice
        const firstWaypoint = route.route_geometry[0];
        if (plan.etd < new Date().getTime() && firstWaypoint && firstWaypoint.properties && (!firstWaypoint.properties.isPassed || firstWaypoint.properties.isPassed === false)) {
            return true;
        }
        return false;
    }
}